import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Briefcase, Plus, Trash2, X, Loader2, TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, RefreshCw, Newspaper, Minus, Download,
} from "lucide-react";
import { Card, EmptyState } from "../common/Primitives";
import api, { formatApiError } from "../../lib/api";

/**
 * PortfolioSection
 * ----------------
 * Investor's invested-startups list with:
 *   - Amount invested, entry valuation, current NAV, MtM multiple per row
 *   - "Update valuation" quick action (mark-to-market)
 *   - Expandable Developments panel per row (profile snapshots + funding
 *     updates + KYC changes, aggregated backend-side)
 */
export default function PortfolioSection({ portfolio, onChange }) {
  const [addOpen, setAddOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const items = portfolio?.items || [];

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/investor/portfolio/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `venturaz_portfolio_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Portfolio CSV downloaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  return (
    <Card testId="section-portfolio">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1.5"><Briefcase size={12} /> Portfolio</div>
          <div className="font-heading text-ink text-lg mt-1">
            Your invested startups
            {items.length > 0 && (
              <span className="ml-2 text-sm text-ink-muted font-mono">({items.length})</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-navy/15 text-ink text-xs font-heading hover:bg-navy/[0.04] disabled:opacity-50 transition-colors"
              data-testid="portfolio-export-csv"
              title="Download the full portfolio as CSV"
            >
              {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Export CSV
            </button>
          )}
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading transition-colors"
            data-testid="portfolio-add-btn"
          >
            <Plus size={12} /> Add Investment
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No investments logged yet"
          hint="Track startups you've backed — either log a manual investment or close a deal room."
        />
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <PortfolioRow key={it.id} item={it} onChange={onChange} />
          ))}
        </div>
      )}

      <AddInvestmentModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); onChange?.(); }} />
    </Card>
  );
}

function PortfolioRow({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [valuationOpen, setValuationOpen] = useState(false);
  const isManual = item.source === "manual";
  const multiple = item.multiple;
  const gain = item.current_nav != null && item.amount ? item.current_nav - item.amount : null;

  const multipleTone = multiple == null ? "neutral"
    : multiple > 1.05 ? "up"
    : multiple < 0.95 ? "down"
    : "flat";

  const remove = async () => {
    if (!isManual) return;
    if (!window.confirm(`Remove ${item.startup_name} from your portfolio?`)) return;
    setBusy(true);
    try {
      await api.delete(`/investor/portfolio/${item.id}`);
      toast.success("Investment removed.");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="border border-white/10 rounded p-3 hover:border-emerald-300 transition-colors"
      data-testid={`portfolio-row-${item.id}`}
    >
      {/* Top row — name / meta / amount / multiple / actions */}
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="flex items-start gap-2 min-w-0 flex-1 text-left"
          data-testid={`portfolio-expand-${item.id}`}
        >
          {expanded
            ? <ChevronDown size={14} className="text-ink-muted mt-1 flex-shrink-0" />
            : <ChevronRight size={14} className="text-ink-muted mt-1 flex-shrink-0" />
          }
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="font-heading text-ink text-sm truncate">{item.startup_name}</span>
              <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-heading uppercase tracking-widest ${
                isManual ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-navy/[0.06] border border-navy/10 text-ink-muted"
              }`}>
                {isManual ? "Manual" : "Deal Room"}
              </span>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              {item.sector || "—"}
              {item.round_stage && <> · {item.round_stage}</>}
              {item.invested_at && <> · {(item.invested_at || "").slice(0, 10)}</>}
            </div>
          </div>
        </button>

        <div className="flex-shrink-0 text-right">
          {item.amount != null && (
            <div className="font-heading text-ink text-sm" data-testid={`portfolio-amount-${item.id}`}>
              ₹{formatInr(item.amount)}
            </div>
          )}
          {multiple != null && (
            <div
              className={`text-[11px] font-mono inline-flex items-center gap-0.5 ${
                multipleTone === "up" ? "text-emerald-700"
                : multipleTone === "down" ? "text-rose-700"
                : "text-ink-muted"
              }`}
              data-testid={`portfolio-multiple-${item.id}`}
            >
              {multipleTone === "up" && <TrendingUp size={10} />}
              {multipleTone === "down" && <TrendingDown size={10} />}
              {multipleTone === "flat" && <Minus size={10} />}
              {multiple.toFixed(2)}x
            </div>
          )}
        </div>

        {isManual && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setValuationOpen(true)}
              className="text-ink-muted hover:text-emerald-700 p-1.5 rounded transition-colors"
              title="Update valuation"
              data-testid={`portfolio-valuation-${item.id}`}
            >
              <RefreshCw size={13} />
            </button>
            <button
              onClick={remove}
              disabled={busy}
              className="text-rose-500 hover:text-rose-700 p-1.5 rounded transition-colors disabled:opacity-50"
              title="Remove from portfolio"
              data-testid={`portfolio-remove-${item.id}`}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            </button>
          </div>
        )}
      </div>

      {/* Expanded panel — valuation history + developments */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-navy/10 space-y-3" data-testid={`portfolio-expanded-${item.id}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Metric label="Invested" value={item.amount ? `₹${formatInr(item.amount)}` : "—"} />
            <Metric label="Holding" value={item.equity_pct != null ? `${item.equity_pct}%` : "—"} />
            <Metric label="Entry Val." value={item.valuation_at_entry ? `₹${formatInr(item.valuation_at_entry)}` : "—"} />
            <Metric
              label="Current NAV"
              value={item.current_nav != null ? `₹${formatInr(item.current_nav)}` : "—"}
              hint={gain != null ? `${gain >= 0 ? "+" : ""}₹${formatInr(gain)}` : null}
              tone={gain == null ? "neutral" : gain > 0 ? "up" : gain < 0 ? "down" : "flat"}
            />
          </div>
          {item.notes && (
            <div className="text-xs text-ink-muted italic border-l-2 border-emerald-200 pl-2" data-testid={`portfolio-notes-${item.id}`}>
              {item.notes}
            </div>
          )}
          <Link
            to={`/startup/${item.startup_id}`}
            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline font-heading"
            data-testid={`portfolio-open-${item.id}`}
          >
            Open startup profile →
          </Link>

          <DevelopmentsPanel startupId={item.startup_id} />
        </div>
      )}

      {valuationOpen && (
        <ValuationModal
          item={item}
          onClose={() => setValuationOpen(false)}
          onSaved={() => { setValuationOpen(false); onChange?.(); }}
        />
      )}
    </div>
  );
}

function Metric({ label, value, hint, tone = "neutral" }) {
  const toneClass = tone === "up" ? "text-emerald-700"
    : tone === "down" ? "text-rose-700"
    : "text-ink";
  return (
    <div className="border border-navy/10 rounded p-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted mb-0.5">{label}</div>
      <div className={`font-heading text-sm ${toneClass}`}>{value}</div>
      {hint && <div className={`text-[10px] font-mono mt-0.5 ${toneClass}`}>{hint}</div>}
    </div>
  );
}

function DevelopmentsPanel({ startupId }) {
  const [loading, setLoading] = useState(true);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/investor/portfolio/${startupId}/developments`)
      .then((r) => { if (!cancelled) setUpdates(r.data?.updates || []); })
      .catch(() => { if (!cancelled) setUpdates([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startupId]);

  return (
    <div data-testid={`portfolio-developments-${startupId}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1.5 flex items-center gap-1">
        <Newspaper size={10} /> Recent Developments
      </div>
      {loading ? (
        <div className="text-xs text-ink-muted">Loading…</div>
      ) : updates.length === 0 ? (
        <div className="text-xs text-ink-muted italic">No recent activity from this startup.</div>
      ) : (
        <div className="space-y-1.5">
          {updates.slice(0, 4).map((u, i) => (
            <div key={i} className="text-xs border-l-2 border-emerald-200 pl-2" data-testid={`portfolio-dev-${startupId}-${i}`}>
              <div className="text-ink font-heading">{u.title}</div>
              {u.body && <div className="text-ink-muted line-clamp-2">{u.body}</div>}
              {u.at && <div className="text-[10px] font-mono text-ink-faint mt-0.5">{u.at.slice(0, 10)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValuationModal({ item, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    current_valuation: item.current_valuation || "",
    current_nav: item.current_nav && !item.equity_pct ? item.current_nav : "",
    note: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_valuation && !form.current_nav) {
      toast.error("Enter either the current valuation or NAV");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/investor/portfolio/${item.id}/valuation`, {
        current_valuation: form.current_valuation ? Number(form.current_valuation) : null,
        current_nav: form.current_nav ? Number(form.current_nav) : null,
        note: form.note || null,
      });
      toast.success("Valuation updated.");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`portfolio-valuation-modal-${item.id}`}>
      <div className="max-w-md w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
          <div className="font-heading text-ink flex items-center gap-2">
            <RefreshCw size={14} className="text-emerald-600" /> Update {item.startup_name}
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="valuation-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {item.equity_pct != null && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800">
              Your holding is <span className="font-heading">{item.equity_pct}%</span> — NAV will be auto-computed as
              equity × current valuation.
            </div>
          )}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Current company valuation (₹)</div>
            <input
              type="number"
              value={form.current_valuation}
              onChange={(e) => setForm((f) => ({ ...f, current_valuation: e.target.value }))}
              placeholder={item.valuation_at_entry ? String(item.valuation_at_entry) : "e.g. 500000000"}
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
              data-testid="valuation-modal-current"
            />
          </div>
          {item.equity_pct == null && (
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">
                Your holding NAV (₹) <span className="text-ink-faint normal-case">— manual override</span>
              </div>
              <input
                type="number"
                value={form.current_nav}
                onChange={(e) => setForm((f) => ({ ...f, current_nav: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
                data-testid="valuation-modal-nav"
              />
            </div>
          )}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Note (optional)</div>
            <input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Series B priced round"
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
              data-testid="valuation-modal-note"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]" data-testid="valuation-modal-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-heading transition-colors"
              data-testid="valuation-modal-submit"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save Valuation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddInvestmentModal({ open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    invested_amount: "",
    invested_at: new Date().toISOString().slice(0, 10),
    equity_pct: "",
    round_stage: "",
    valuation_at_entry: "",
    current_valuation: "",
    notes: "",
  });

  useEffect(() => {
    if (!open) {
      setQuery(""); setCandidates([]); setSelected(null);
      setForm({
        invested_amount: "",
        invested_at: new Date().toISOString().slice(0, 10),
        equity_pct: "",
        round_stage: "",
        valuation_at_entry: "",
        current_valuation: "",
        notes: "",
      });
    }
  }, [open]);

  const searchStartups = async (q) => {
    setQuery(q);
    if ((q || "").length < 2) { setCandidates([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}&limit=8`);
      const hits = (res.data?.results || []).filter((h) => h.type === "startup").map((h) => ({
        startup_id: h.id,
        startup_name: h.title,
        sector: (h.subtitle || "").split("·")[0].trim(),
      }));
      setCandidates(hits);
    } catch (e) { /* silent */ }
    finally { setSearching(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Pick a startup first"); return; }
    if (!form.invested_amount) { toast.error("Enter the invested amount"); return; }
    setSaving(true);
    try {
      await api.post("/investor/portfolio", {
        startup_id: selected.startup_id,
        invested_amount: Number(form.invested_amount),
        invested_at: form.invested_at ? `${form.invested_at}T00:00:00+00:00` : null,
        equity_pct: form.equity_pct ? Number(form.equity_pct) : null,
        round_stage: form.round_stage || null,
        valuation_at_entry: form.valuation_at_entry ? Number(form.valuation_at_entry) : null,
        current_valuation: form.current_valuation ? Number(form.current_valuation) : null,
        notes: form.notes || null,
      });
      toast.success("Investment logged.");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="portfolio-add-modal" role="dialog">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-heading text-lg text-ink flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-600" /> Log an Investment
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="portfolio-add-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Startup</div>
            {selected ? (
              <div className="flex items-center justify-between border border-emerald-300 bg-emerald-50/40 p-2 rounded" data-testid="portfolio-add-selected">
                <div className="min-w-0">
                  <div className="font-heading text-sm text-ink truncate">{selected.startup_name || selected.name}</div>
                  <div className="text-[11px] font-mono uppercase text-ink-muted">{selected.sector || "—"}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-ink-muted hover:text-ink text-xs">Change</button>
              </div>
            ) : (
              <>
                <input
                  value={query}
                  onChange={(e) => searchStartups(e.target.value)}
                  placeholder="Search by startup name…"
                  className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
                  data-testid="portfolio-add-search"
                />
                {searching && <div className="text-xs text-ink-muted mt-1">Searching…</div>}
                {candidates.length > 0 && (
                  <div className="mt-2 border border-navy/10 rounded max-h-40 overflow-y-auto bg-white shadow-sm">
                    {candidates.map((c) => (
                      <button type="button" key={c.startup_id} onClick={() => { setSelected(c); setCandidates([]); setQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b border-navy/5 last:border-0" data-testid={`portfolio-add-candidate-${c.startup_id}`}>
                        <div className="font-heading text-ink">{c.startup_name || c.name}</div>
                        <div className="text-[10px] font-mono uppercase text-ink-muted">{c.sector || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <LinkedAmountEquityValuation
              amount={form.invested_amount}
              equity={form.equity_pct}
              valuation={form.valuation_at_entry}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
            <Field label="Date" type="date" value={form.invested_at} onChange={(e) => setForm((f) => ({ ...f, invested_at: e.target.value }))} testId="portfolio-add-date" />
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Round</div>
              <select value={form.round_stage} onChange={(e) => setForm((f) => ({ ...f, round_stage: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm bg-white" data-testid="portfolio-add-round">
                <option value="">Select round</option>
                {["Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth", "Bridge", "Convertible"].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <Field label="Current Valuation (₹)" type="number" value={form.current_valuation} onChange={(e) => setForm((f) => ({ ...f, current_valuation: e.target.value }))} placeholder="Optional" testId="portfolio-add-current-val" />
          </div>

          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Optional — lead investor, board seat, milestones…"
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm resize-none"
              data-testid="portfolio-add-notes"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]" data-testid="portfolio-add-cancel">Cancel</button>
            <button type="submit" disabled={saving || !selected} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-heading transition-colors" data-testid="portfolio-add-submit">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save Investment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", step, testId, required }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <input
        type={type}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * LinkedAmountEquityValuation
 * ---------------------------
 * 3 linked inputs where any two auto-derive the third:
 *   amount = equity_pct × valuation_at_entry / 100
 *   equity_pct = amount / valuation_at_entry × 100
 *   valuation_at_entry = amount / (equity_pct / 100)
 *
 * The user's most-recently-edited field takes precedence — subsequent edits
 * to other fields will recalculate leaving that one alone. A per-field 🔗/✎
 * indicator + override button lets the investor lock or manually override
 * any auto-computed value at any time.
 */
function LinkedAmountEquityValuation({ amount, equity, valuation, onChange }) {
  // Track the two most-recently-edited fields so we know which one to derive.
  // The rule: when the user edits field X, we recompute the field that WASN'T
  // the second-most-recently edited (i.e. the stalest of the other two).
  const [editOrder, setEditOrder] = React.useState([]); // e.g. ['amount','equity']
  const [overrides, setOverrides] = React.useState({}); // manual locks

  const toNum = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

  const deriveField = (edited) => {
    // Return the target field to auto-fill. Prefer the field the user has NOT
    // touched recently; skip fields the user has manually locked.
    const others = ["amount", "equity", "valuation"].filter((f) => f !== edited);
    // Filter locked ones out.
    const candidates = others.filter((f) => !overrides[f]);
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];
    // Pick the field NOT in editOrder[0] (the most-recently-edited of the two others).
    const recent = editOrder.find((f) => candidates.includes(f));
    const target = candidates.find((f) => f !== recent);
    return target || candidates[0];
  };

  const compute = (edited, next) => {
    const a = toNum(next.amount);
    const e = toNum(next.equity);
    const v = toNum(next.valuation);
    const target = deriveField(edited);
    if (!target) return {};
    if (target === "amount" && e != null && v != null && v > 0) {
      return { amount: (v * (e / 100)).toFixed(0) };
    }
    if (target === "equity" && a != null && v != null && v > 0) {
      return { equity: ((a / v) * 100).toFixed(4) };
    }
    if (target === "valuation" && a != null && e != null && e > 0) {
      return { valuation: (a / (e / 100)).toFixed(0) };
    }
    return {};
  };

  const setField = (key) => (e) => {
    const raw = e.target.value;
    // Track this as the newest edit; also clear the manual lock on this field
    // since the user is explicitly re-typing it.
    setEditOrder((prev) => [key, ...prev.filter((f) => f !== key)].slice(0, 3));
    if (overrides[key]) setOverrides((o) => ({ ...o, [key]: false }));

    const next = { amount, equity, valuation, [key]: raw };
    const derived = compute(key, next);

    onChange({
      invested_amount: key === "amount" ? raw : (derived.amount ?? amount),
      equity_pct: key === "equity" ? raw : (derived.equity ?? equity),
      valuation_at_entry: key === "valuation" ? raw : (derived.valuation ?? valuation),
    });
  };

  const toggleOverride = (key) => setOverrides((o) => ({ ...o, [key]: !o[key] }));

  const LinkBadge = ({ locked, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      title={locked ? "Override active — click to re-enable auto-calc" : "Click to lock manual override"}
      className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors ${
        locked ? "border-amber-400 text-amber-700 bg-amber-50" : "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
      }`}
      data-testid={testId}
    >
      {locked ? "Override" : "Auto"}
    </button>
  );

  return (
    <>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Amount (₹)</div>
        <div className="relative">
          <input
            type="number"
            value={amount || ""}
            onChange={setField("amount")}
            placeholder="5000000"
            required
            className="w-full px-3 py-2 pr-20 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
            data-testid="portfolio-add-amount"
          />
          <LinkBadge locked={overrides.amount} onClick={() => toggleOverride("amount")} testId="portfolio-add-amount-lock" />
        </div>
      </div>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Equity (%)</div>
        <div className="relative">
          <input
            type="number"
            step="0.01"
            value={equity || ""}
            onChange={setField("equity")}
            placeholder="2.5"
            className="w-full px-3 py-2 pr-20 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
            data-testid="portfolio-add-equity"
          />
          <LinkBadge locked={overrides.equity} onClick={() => toggleOverride("equity")} testId="portfolio-add-equity-lock" />
        </div>
      </div>
      <div className="col-span-2">
        <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Entry Valuation (₹)</div>
        <div className="relative">
          <input
            type="number"
            value={valuation || ""}
            onChange={setField("valuation")}
            placeholder="200000000"
            className="w-full px-3 py-2 pr-20 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
            data-testid="portfolio-add-entry-val"
          />
          <LinkBadge locked={overrides.valuation} onClick={() => toggleOverride("valuation")} testId="portfolio-add-entry-val-lock" />
        </div>
        {amount && equity && valuation && (
          <div className="text-[11px] text-emerald-700 font-mono mt-1" data-testid="portfolio-add-calc-preview">
            {`= ${Number(amount).toLocaleString("en-IN")} ÷ (${Number(equity)}% ) ≈ ₹${Number(valuation).toLocaleString("en-IN")}`}
          </div>
        )}
      </div>
    </>
  );
}

function formatInr(n) {
  if (!n || Number.isNaN(n)) return "0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}${(abs / 1e5).toFixed(1)} L`;
  return sign + abs.toLocaleString("en-IN");
}
